import * as React from 'react';

import { once } from 'lodash';
import pluralize from 'pluralize';

import AdminSchoolSisDashboardIssueTable from 'admin/rostering/components/AdminSchoolSisDashboardIssueTable';
import AdminSchoolSisDashboardSummary from 'admin/rostering/components/AdminSchoolSisDashboardSummary';
import AdminSchoolSisSyncHeader from 'admin/rostering/components/AdminSchoolSisSyncHeader';
import AdminSisSyncNotificationOptions from 'admin/rostering/components/AdminSisSyncNotificationOptions';
import Link from 'components/Link';
import Row from 'components/Row';
import Text from 'components/Text';
import LinkConstants from 'constants/LinkConstants';
import EventTracking from 'lib/EventTracking';
import Time from 'components/Time';

import {
  AdminSchoolSisDashboard_organizations_sisSyncOverview as SisSyncOverview,
  AdminSchoolSisDashboard_organizations_sisSyncOverview_issueSummaries as IssueSummary,
} from 'admin/rostering/queries/__generated__/AdminSchoolSisDashboard';

type Props = {
  orgUuid: string;
  orgId: number;
  sisSyncOverview: SisSyncOverview;
  isStagingArea: boolean;
};

export default class AdminSisSyncConnectedView extends React.Component<
  Props & React.HTMLAttributes<HTMLDivElement>
> {
  componentWillMount() {
    this.trackView();
  }

  renderNotificationOptions() {
    const { orgUuid } = this.props;
    const { canNotifyTeachers, canNotifyStudents, canNotifyParents } = this.props.sisSyncOverview;
    const canNotify = canNotifyTeachers || canNotifyStudents || canNotifyParents;
    return (
      canNotify && (
        <Row>
          <AdminSisSyncNotificationOptions
            orgUuid={orgUuid}
            canNotifyTeachers={canNotifyTeachers}
            canNotifyStudents={canNotifyStudents}
            canNotifyParents={canNotifyParents}
          />
        </Row>
      )
    );
  }

  genericCountText() {
    const { parentCount, studentCount, teacherCount } = this.props.sisSyncOverview;

    if (parentCount == -1 || studentCount == -1 || teacherCount == -1) {
      return <Text>School data</Text>;
    } else {
      return null;
    }
  }

  specificCountText() {
    const { parentCount, studentCount, teacherCount } = this.props.sisSyncOverview;
    return (
      <Text>
        <Text weight="semibold">{pluralize('teacher', teacherCount, true)}</Text>,{' '}
        <Text weight="semibold">{pluralize('student', studentCount, true)}</Text>, and{' '}
        <Text weight="semibold">{pluralize('parent', parentCount, true)}</Text>
      </Text>
    );
  }

  renderStagingSubtext(date: JSX.Element | null) {
    return (
      <Text>
        {this.genericCountText() || this.specificCountText()} sent from your SIS
        {date && <Text> on {date}</Text>}. The data is not live yet.
      </Text>
    );
  }

  renderDashboardSubtext(date: JSX.Element | null) {
    return (
      <Text>
        {this.genericCountText() || this.specificCountText()} added from your SIS{' '}
        {date && <Text> on {date}</Text>}.
      </Text>
    );
  }

  renderHeader() {
    const { lastSyncTimestamp } = this.props.sisSyncOverview;
    const { isStagingArea } = this.props;
    const date = lastSyncTimestamp ? (
      <Time
        date={new Date(lastSyncTimestamp * 1000)}
        month="long"
        day="numeric"
        hour12
        hour="numeric"
        minute="2-digit"
      />
    ) : null;

    return (
      <AdminSchoolSisSyncHeader
        subtext={
          isStagingArea ? this.renderStagingSubtext(date) : this.renderDashboardSubtext(date)
        }
        status={isStagingArea ? 'pending' : 'connected'}
      />
    );
  }
  renderSummary() {
    const {
      orgUuid,
      sisSyncOverview: { errorCount, alertCount, allIssuesDownloadUrl },
    } = this.props;

    return (
      <Row>
        <AdminSchoolSisDashboardSummary
          errors={errorCount}
          alerts={alertCount}
          csvUrl={allIssuesDownloadUrl ?? undefined}
          orgUuid={orgUuid}
        />
      </Row>
    );
  }

  renderErrorsTable() {
    const {
      orgUuid,
      sisSyncOverview: { errorCount, issueSummaries },
    } = this.props;

    if (errorCount === 0) {
      return null;
    }

    const subtext = (
      <Text color="graphite">
        There are errors in your SIS that are preventing user accounts or classes from being
        created. Read about them below and fix them directly in your SIS.{' '}
        <Link href={LinkConstants.UPDATE_SIS} weight="semibold" target="_blank">
          Learn more about how to resolve errors and alerts here.
        </Link>
      </Text>
    );

    return (
      <Row>
        <AdminSchoolSisDashboardIssueTable
          type="Error"
          subtext={subtext}
          orgUuid={orgUuid}
          issues={issueSummaries.filter((i: IssueSummary) => i.severity === 'error')}
        />
      </Row>
    );
  }

  renderAlertsTable() {
    const {
      orgUuid,
      sisSyncOverview: { alertCount, issueSummaries },
    } = this.props;

    if (alertCount === 0) {
      return null;
    }

    const subtext = (
      <Text color="graphite">
        Alerts are minor data errors in your SIS that, if fixed, would make Remind more useful and
        productive to use. We recommend you address them in your SIS to fully enjoy using Remind.{' '}
        <Link href={LinkConstants.UPDATE_SIS} weight="semibold" target="_blank">
          Learn more about how to resolve errors and alerts here.
        </Link>
      </Text>
    );

    return (
      <Row>
        <AdminSchoolSisDashboardIssueTable
          type="Alert"
          subtext={subtext}
          orgUuid={orgUuid}
          issues={issueSummaries.filter((i: IssueSummary) => i.severity === 'alert')}
        />
      </Row>
    );
  }

  renderNoErrorsInfo() {
    const {
      orgId,
      sisSyncOverview: { errorCount, alertCount },
      isStagingArea,
    } = this.props;
    const slug = `/organizations/${orgId}`;

    if (alertCount + errorCount !== 0 || isStagingArea) {
      return null;
    }

    return (
      <Text>
        There were no problems with your most recent sync. If you’d like to check how rostering is
        shaping Remind, check the{' '}
        <Link to={`${slug}/people`} weight="semibold">
          People
        </Link>{' '}
        and{' '}
        <Link to={`${slug}/classes`} weight="semibold">
          Classes
        </Link>{' '}
        views for new people and classes.
      </Text>
    );
  }

  trackView = once(() => {
    EventTracking.view('rostering.sis_dash.connected.view', {
      organization_uud: this.props.orgUuid,
    });
  });

  render() {
    const { isStagingArea } = this.props;
    return (
      <section className="AdminSisSyncConnectedView">
        {this.renderHeader()}
        {!isStagingArea && this.renderNotificationOptions()}
        {this.renderSummary()}
        {this.renderErrorsTable()}
        {this.renderAlertsTable()}
        {this.renderNoErrorsInfo()}
      </section>
    );
  }
}
